<template>
  <div
    class="match-anno-panel"
    ref="container"
    @wheel.prevent="onWheel"
    @mousedown.right="onPanStart"
    @mousemove="onPanMove"
    @mouseup.right="onPanEnd"
    @mouseleave="onPanEnd"
    @contextmenu.prevent
  >
    <div class="image-wrapper" :style="wrapperStyle">
      <img
        :src="imageReformat(src)"
        :style="{
          width: imgWidth + 'px',
          height: imgHeight + 'px',
          display: 'block',
        }"
        draggable="false"
      />
    </div>
    <div
      v-for="anno in annotations"
      :key="anno.id"
      class="anno-box"
      :class="{
        selected: anno.id === selectedId,
        matched: !!anno.groupNo,
        'no-select': disableAnnoIds && disableAnnoIds.includes(anno.id),
      }"
      :style="getAnnoStyle(anno)"
      @click.stop="handleAnnoClick(anno)"
    >
      <span
        v-if="anno.groupNo"
        class="group-label"
        :style="{ background: anno.id === selectedId ? selectedColor : color }"
        >{{ anno.groupNo }}</span
      >
    </div>
  </div>
</template>

<script>
import { imageReformat } from '@/utils/util';

export default {
  name: 'MatchAnnoPanel',
  props: {
    src: String,
    annotations: Array,
    imgWidth: Number,
    imgHeight: Number,
    selectedId: Number,
    color: { type: String, default: '#5d89c0' },
    selectedColor: { type: String, default: '#ff6b6b' },
    disableAnnoIds: Array,
    flipH: Boolean,
    flipV: Boolean,
  },
  data() {
    return {
      fitScale: 1,
      zoom: 1,
      panX: 0,
      panY: 0,
      panning: false,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      contW: 0,
      contH: 0,
    };
  },
  computed: {
    wrapperStyle() {
      const s = this.fitScale * this.zoom;
      let flipTransform = '';
      if (this.flipH) flipTransform += ' scaleX(-1)';
      if (this.flipV) flipTransform += ' scaleY(-1)';
      return {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: this.imgWidth + 'px',
        height: this.imgHeight + 'px',
        transform: `translate(calc(-50% + ${this.panX}px), calc(-50% + ${this.panY}px)) scale(${s})${flipTransform}`,
        transformOrigin: 'center center',
      };
    },
  },
  mounted() {
    this.updateContainerSize();
    this.$nextTick(() => this.updateContainerSize());
    window.addEventListener('resize', this.updateContainerSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerSize);
  },
  watch: {
    src() {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
      this.$nextTick(() => this.updateContainerSize());
    },
  },
  methods: {
    imageReformat,
    updateContainerSize() {
      const el = this.$refs.container;
      if (!el || !this.imgWidth || !this.imgHeight) return;
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      this.contW = cw;
      this.contH = ch;
      const sx = cw / this.imgWidth;
      const sy = ch / this.imgHeight;
      this.fitScale = Math.min(sx, sy);
    },
    // Annotation position in container pixel space (unaffected by wrapper transform)
    getAnnoStyle(anno) {
      const s = this.fitScale * this.zoom;
      let [x1, y1, x2, y2] = anno.points;
      if (x1 > x2) [x1, x2] = [x2, x1];
      if (y1 > y2) [y1, y2] = [y2, y1];

      if (this.flipH) {
        const nx1 = this.imgWidth - x2;
        x2 = this.imgWidth - x1;
        x1 = nx1;
      }
      if (this.flipV) {
        const ny1 = this.imgHeight - y2;
        y2 = this.imgHeight - y1;
        y1 = ny1;
      }

      const halfW = this.contW / 2;
      const halfH = this.contH / 2;
      const color = anno.id === this.selectedId ? this.selectedColor : this.color;
      return {
        position: 'absolute',
        left: halfW + this.panX + (x1 - this.imgWidth / 2) * s + 'px',
        top: halfH + this.panY + (y1 - this.imgHeight / 2) * s + 'px',
        width: (x2 - x1) * s + 'px',
        height: (y2 - y1) * s + 'px',
        borderColor: color,
        '--box-bg': color + '26',
      };
    },
    handleAnnoClick(anno) {
      if (anno.groupNo) return;
      if (this.disableAnnoIds && this.disableAnnoIds.includes(anno.id)) return;
      this.$emit('annoClick', anno.id);
    },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoom = Math.max(1, Math.min(20, this.zoom + delta));
    },
    onPanStart(e) {
      this.panning = true;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panOriginX = this.panX;
      this.panOriginY = this.panY;
    },
    onPanMove(e) {
      if (!this.panning) return;
      this.panX = this.panOriginX + (e.clientX - this.panStartX);
      this.panY = this.panOriginY + (e.clientY - this.panStartY);
    },
    onPanEnd() {
      this.panning = false;
    },
    resetView() {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
      this.updateContainerSize();
    },
  },
};
</script>

<style scoped>
.match-anno-panel {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background: #e8e8e8;
}
.image-wrapper {
  z-index: 1;
}
.image-wrapper > img {
  position: relative;
  z-index: 1;
}
.anno-box {
  position: absolute;
  border: 3px solid;
  border-radius: 2px;
  cursor: pointer;
  z-index: 2;
  box-sizing: border-box;
}
.anno-box:hover {
  background: var(--box-bg, transparent);
}
.anno-box.selected {
  background: rgba(255, 107, 107, 0.08);
}
.anno-box.matched {
  opacity: 0.6;
  cursor: default;
}
.anno-box.matched:hover {
  background: transparent;
}
.anno-box.no-select {
  cursor: default;
}
.group-label {
  position: absolute;
  top: -1px;
  right: -1px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  min-width: 20px;
  text-align: center;
  border-radius: 0 2px 0 2px;
  padding: 0 5px;
  pointer-events: none;
}
</style>
